import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  achievementTypeConfig,
  locationConfig,
  achievementStats,
  liveAchievements,
  achievementRules,
  getAchievementsByType,
  getAchievementsByLocation,
  getRecentAchievements,
  getActiveAchievements,
  getAchievementsByStyle,
  type AchievementType,
  type AchievementLocation,
  type AchievementStyle,
  type LiveAchievement,
} from '@/mocks/rfwLiveAchievements';

type PageState = 'loading' | 'error' | 'ready';
type ViewSection = 'en-vivo' | 'ubicaciones' | 'tipos' | 'reglas';

function TypeBadge({ type }: { type: AchievementType }) {
  const c = achievementTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function LocationBadge({ location }: { location: AchievementLocation }) {
  const c = locationConfig[location];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function DecayTimer({ decayMinutes }: { decayMinutes: number }) {
  const label = decayMinutes >= 1440
    ? `${Math.floor(decayMinutes / 1440)}d`
    : decayMinutes >= 60
    ? `${Math.floor(decayMinutes / 60)}h`
    : `${decayMinutes}m`;
  return (
    <span className="inline-flex items-center gap-1 text-[8px] text-foreground-400">
      <i className="ri-timer-line text-[7px]"></i>
      ~{label}
    </span>
  );
}

export default function AchievementsEnVivoPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeView, setActiveView] = useState<ViewSection>('en-vivo');
  const [typeFilter, setTypeFilter] = useState<AchievementType | 'todas'>('todas');
  const [locationFilter, setLocationFilter] = useState<AchievementLocation | 'todas'>('todas');
  const [styleFilter, setStyleFilter] = useState<AchievementStyle | 'todas'>('todas');
  const [sortBy, setSortBy] = useState<'recent' | 'reactions' | 'decay'>('recent');
  const [selectedAchievement, setSelectedAchievement] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setPageState('ready');
      setTimeout(() => setShowCelebration(true), 200);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      setPageState('ready');
      setShowCelebration(true);
    }, 400);
  }, []);

  const filtered = useMemo(() => {
    let result = typeFilter === 'todas'
      ? (locationFilter === 'todas' ? liveAchievements : getAchievementsByLocation(locationFilter))
      : getAchievementsByType(typeFilter).filter((a) => locationFilter === 'todas' ? true : a.location === locationFilter);
    if (styleFilter !== 'todas') result = result.filter((a) => a.style === styleFilter);
    if (sortBy === 'reactions') result = [...result].sort((a, b) => b.reactions - a.reactions);
    else if (sortBy === 'decay') result = [...result].sort((a, b) => a.decayMinutes - b.decayMinutes);
    return result;
  }, [typeFilter, locationFilter, styleFilter, sortBy]);

  const activeCount = useMemo(() => getActiveAchievements().length, []);
  const recentCount = useMemo(() => getRecentAchievements().length, []);

  const views: { id: ViewSection; label: string; icon: string; badge?: number }[] = [
    { id: 'en-vivo', label: 'En Vivo', icon: 'ri-sparkling-line', badge: recentCount },
    { id: 'ubicaciones', label: 'Ubicaciones', icon: 'ri-map-pin-line', badge: 4 },
    { id: 'tipos', label: 'Tipos de Logro', icon: 'ri-shapes-line', badge: 5 },
    { id: 'reglas', label: 'Reglas', icon: 'ri-file-list-3-line', badge: 3 },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[200px] md:h-[260px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
            <div className="md:col-span-2 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar achievements</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los achievements en vivo. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20celebration%20concept%20with%20floating%20sparkling%20particles%20confetti%20and%20glowing%20achievement%20badges%20rising%20upward%20elegant%20teal%20amber%20gold%20tones%20smooth%20gradients%20celebration%20atmosphere%20editorial%20photography%20clean%20soft%20lighting&width=1600&height=900&seq=rfw-achievements-hero&orientation=landscape"
          alt="Achievements en Vivo RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className={`w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20 transition-all duration-700 ${showCelebration ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}>
            <i className="ri-sparkling-line text-3xl text-background-50"></i>
          </div>
          <h1 className={`text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3 transition-all duration-700 delay-100 ${showCelebration ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            Achievements en Vivo
          </h1>
          <p className={`text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed transition-all duration-700 delay-200 ${showCelebration ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            Nuevos niveles, insignias, récords, rankings y logros — celebrados en el momento justo, en el lugar correcto, sin ser invasivos. Cada achievement motiva, cada badge inspira.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* LIVE PULSE BAR */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Logros Hoy', value: achievementStats.totalToday.toLocaleString(), icon: 'ri-sparkling-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Activos Ahora', value: activeCount.toString(), icon: 'ri-live-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Niveles', value: achievementStats.porNivel.toString(), icon: 'ri-arrow-up-circle-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Insignias', value: achievementStats.porInsignia.toString(), icon: 'ri-award-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Récords', value: achievementStats.porRecord.toString(), icon: 'ri-fire-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Rankings', value: achievementStats.porRanking.toString(), icon: 'ri-trophy-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-xs`}></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-foreground-950">{stat.value}</p>
                <p className="text-[9px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* SIDEBAR */}
          <aside className="md:col-span-3">
            <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden sticky top-4">
              <div className="p-4 border-b border-background-200/50">
                <h2 className="text-sm font-extrabold text-foreground-950 mb-1">Achievements</h2>
                <p className="text-[10px] text-foreground-500">Celebrando logros en vivo</p>
              </div>

              <nav className="p-2">
                {views.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setActiveView(v.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 cursor-pointer mb-1 ${
                      activeView === v.id
                        ? 'bg-teal-50 text-teal-700 font-bold'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${v.icon} text-sm ${activeView === v.id ? 'text-teal-600' : ''}`}></i>
                    <span className="text-xs flex-1">{v.label}</span>
                    {v.badge !== undefined && v.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${
                        activeView === v.id ? 'bg-teal-200 text-teal-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        {v.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              <div className="p-3 mx-2 mb-2 rounded-xl bg-background-100">
                <p className="text-[10px] font-bold text-foreground-600 mb-2">Estilos de Animación</p>
                <div className="space-y-1.5">
                  {[
                    { key: 'celebratory' as const, label: '🎉 Celebratorio', desc: 'Cabina — efímero e intenso', color: 'text-emerald-600' },
                    { key: 'moderate' as const, label: '✨ Moderado', desc: 'Feed y Club — visible', color: 'text-amber-600' },
                    { key: 'subtle' as const, label: '🔹 Sutil', desc: 'Perfil — permanente', color: 'text-teal-600' },
                  ].map((s) => (
                    <button
                      key={s.key}
                      onClick={() => { setActiveView('en-vivo'); setStyleFilter(s.key); }}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-[9px] transition-colors cursor-pointer ${
                        styleFilter === s.key ? 'bg-teal-50 font-semibold text-teal-700' : 'text-foreground-500 hover:bg-background-50'
                      }`}
                    >
                      <span className={s.color}>{s.label}</span>
                      <span className="block text-[8px] text-foreground-400">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <div className="md:col-span-9">
            {/* VIEW: EN VIVO */}
            {activeView === 'en-vivo' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {([
                      { id: 'todas' as const, label: 'Todos', icon: 'ri-list-check-3' },
                      ...Object.entries(achievementTypeConfig).map(([key, c]) => ({
                        id: key as AchievementType,
                        label: c.label,
                        icon: c.icon,
                      })),
                    ]).map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setTypeFilter(f.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                          typeFilter === f.id
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                        }`}
                      >
                        <i className={`${f.icon} text-[9px]`}></i>
                        {f.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5">
                    {([
                      { id: 'recent' as const, label: 'Recientes', icon: 'ri-time-line' },
                      { id: 'reactions' as const, label: 'Más Reacciones', icon: 'ri-heart-line' },
                      { id: 'decay' as const, label: 'Más Efímeros', icon: 'ri-timer-line' },
                    ]).map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSortBy(s.id)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                          sortBy === s.id
                            ? 'bg-teal-500 text-white border-teal-500'
                            : 'bg-background-50 text-foreground-500 border-background-200 hover:border-teal-300'
                        }`}
                      >
                        <i className={`${s.icon} text-[9px]`}></i>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todas las Ubicaciones', icon: 'ri-list-check-3' },
                    ...Object.entries(locationConfig).map(([key, c]) => ({
                      id: key as AchievementLocation,
                      label: c.label,
                      icon: c.icon,
                    })),
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setLocationFilter(f.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        locationFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>

                {styleFilter !== 'todas' && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-foreground-400">Estilo:</span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-[10px] font-semibold border border-teal-200/50">
                      {styleFilter === 'celebratory' ? '🎉 Celebratorio' : styleFilter === 'moderate' ? '✨ Moderado' : '🔹 Sutil'}
                      <button onClick={() => setStyleFilter('todas')} className="ml-1 w-4 h-4 rounded-full bg-teal-200 flex items-center justify-center cursor-pointer">
                        <i className="ri-close-line text-[8px] text-teal-600"></i>
                      </button>
                    </span>
                  </div>
                )}

                <p className="text-[10px] text-foreground-400">
                  {filtered.length} achievement{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {filtered.map((achievement) => (
                    <AchievementDetailCard
                      key={achievement.id}
                      achievement={achievement}
                      isSelected={selectedAchievement === achievement.id}
                      onClick={() => setSelectedAchievement(selectedAchievement === achievement.id ? null : achievement.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: UBICACIONES */}
            {activeView === 'ubicaciones' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5">
                  {Object.entries(locationConfig).map(([key, c]) => {
                    const achievements = getAchievementsByLocation(key as AchievementLocation);
                    const recent = achievements.filter((a) => a.isRecent);
                    return (
                      <div key={key} className={`p-5 rounded-2xl bg-background-50 border border-background-200/70`}>
                        <div className="flex items-start gap-4 mb-5">
                          <div className={`w-16 h-16 rounded-2xl ${c.bgColor} flex items-center justify-center shrink-0`}>
                            <i className={`${c.icon} text-3xl ${c.color}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-extrabold text-foreground-950 mb-1">{c.label}</h3>
                            <p className="text-xs text-foreground-500 leading-relaxed">{c.description}</p>
                          </div>
                          <div className="text-center shrink-0 space-y-2">
                            <div>
                              <p className="text-2xl font-extrabold text-foreground-950">{achievements.length}</p>
                              <p className="text-[9px] text-foreground-400">totales</p>
                            </div>
                            {recent.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                                {recent.length} activos
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {achievements.map((a) => (
                            <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl bg-background-100">
                              <div className="relative shrink-0">
                                <img src={a.userPhoto} alt={a.userName} className="w-10 h-10 rounded-full object-cover" />
                                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${achievementTypeConfig[a.achievementType].bgColor} flex items-center justify-center border-2 border-background-100`}>
                                  <i className={`${achievementTypeConfig[a.achievementType].icon} text-[7px] ${achievementTypeConfig[a.achievementType].color}`}></i>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <TypeBadge type={a.achievementType} />
                                  {a.isRecent && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-emerald-100 text-emerald-700">
                                      <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                                      AHORA
                                    </span>
                                  )}
                                </div>
                                <p className="text-[10px] font-semibold text-foreground-950 line-clamp-1">{a.title}</p>
                                <p className="text-[8px] text-foreground-400">{a.userName} · {a.achievedAtRelative}</p>
                              </div>
                              <DecayTimer decayMinutes={a.decayMinutes} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VIEW: TIPOS */}
            {activeView === 'tipos' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5">
                  {Object.entries(achievementTypeConfig).map(([key, c]) => {
                    const achievements = getAchievementsByType(key as AchievementType);
                    return (
                      <div key={key} className={`p-5 rounded-2xl bg-gradient-to-br ${c.gradient} border ${c.borderColor}`}>
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`w-16 h-16 rounded-2xl ${c.bgColor} flex items-center justify-center shrink-0`}>
                            <i className={`${c.icon} text-3xl ${c.color}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-extrabold text-foreground-950 mb-1">{c.label}</h3>
                            <p className="text-xs text-foreground-500 leading-relaxed">{c.description}</p>
                          </div>
                          <div className="text-center shrink-0">
                            <p className="text-2xl font-extrabold text-foreground-950">{achievements.length}</p>
                            <p className="text-[9px] text-foreground-400">ejemplos</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {achievements.map((a) => (
                            <div key={a.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-background-50/70">
                              <img src={a.userPhoto} alt={a.userName} className="w-8 h-8 rounded-full object-cover shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-semibold text-foreground-950 line-clamp-1">{a.title}</p>
                                <div className="flex items-center gap-2 text-[8px] text-foreground-400">
                                  <span>{a.userName}</span>
                                  <LocationBadge location={a.location} />
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <span className={`text-[10px] font-bold ${c.color}`}>{a.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VIEW: REGLAS */}
            {activeView === 'reglas' && (
              <div className="space-y-5">
                {achievementRules.map((rule, i) => (
                  <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center shrink-0 mt-1">
                        <i className={`${rule.icon} text-xl ${rule.color}`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Regla #{i + 1}</span>
                        </div>
                        <h3 className="text-base font-extrabold text-foreground-950 mb-2">{rule.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{rule.description}</p>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <h3 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-pulse-line text-teal-500"></i>
                    Ciclo de Vida de un Achievement
                  </h3>
                  <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2">
                    {[
                      { label: 'Se Dispara', icon: 'ri-flashlight-line', desc: 'El oyente alcanza el logro', color: 'text-teal-600 bg-teal-50' },
                      { label: 'Se Muestra', icon: 'ri-eye-line', desc: 'Aparece en la ubicación correcta', color: 'text-amber-600 bg-amber-50' },
                      { label: 'Se Celebra', icon: 'ri-sparkling-line', desc: 'Animación sutil, reacciones', color: 'text-rose-600 bg-rose-50' },
                      { label: 'Decae', icon: 'ri-timer-line', desc: 'Desaparece naturalmente', color: 'text-violet-600 bg-violet-50' },
                      { label: 'Se Archiva', icon: 'ri-archive-line', desc: 'Queda en el perfil (si suma)', color: 'text-sky-600 bg-sky-50' },
                    ].map((step, idx) => (
                      <div key={idx} className="flex items-center gap-0 shrink-0">
                        <div className="flex flex-col items-center text-center p-3 rounded-xl bg-background-100 min-w-[100px]">
                          <div className={`w-8 h-8 rounded-full ${step.color} flex items-center justify-center mb-1.5`}>
                            <i className={`${step.icon} text-xs`}></i>
                          </div>
                          <p className="text-[10px] font-bold text-foreground-950 mb-0.5">{step.label}</p>
                          <p className="text-[8px] text-foreground-400">{step.desc}</p>
                        </div>
                        {idx < 4 && (
                          <i className="ri-arrow-right-line text-foreground-300 text-xs mx-1 shrink-0"></i>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-10 mb-6">
          {[
            { to: '/gamificacion', icon: 'ri-gamepad-line', iconBg: 'bg-teal-100', iconColor: 'text-teal-600', label: 'Gamificación', desc: 'Misiones y recompensas' },
            { to: '/niveles', icon: 'ri-bar-chart-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Niveles', desc: 'Sistema de progresión' },
            { to: '/insignias', icon: 'ri-award-line', iconBg: 'bg-rose-100', iconColor: 'text-rose-600', label: 'Insignias', desc: 'Colección de logros' },
            { to: '/rankings', icon: 'ri-trophy-line', iconBg: 'bg-violet-100', iconColor: 'text-violet-600', label: 'Rankings', desc: 'Los mejores de RFW' },
          ].map((link, i) => (
            <Link key={i} to={link.to} className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-teal-300/60 transition-all duration-200 text-center">
              <div className={`w-10 h-10 rounded-full ${link.iconBg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${link.icon} text-base ${link.iconColor}`}></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">{link.label}</p>
              <p className="text-[10px] text-foreground-500 mt-1">{link.desc}</p>
            </Link>
          ))}
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-teal-100/70 border border-teal-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-sparkling-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Celebrar logros sin ser invasivo.</p>
          <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
            En RFW cada achievement está diseñado para motivar, no para interrumpir. En la cabina duran minutos — lo justo para que la comunidad reaccione y celebre. En el feed se muestran con elegancia, sin robar el scroll. En los clubes generan pertenencia sin saturar. Y en el perfil quedan como memoria permanente de cada paso en el camino del oyente. Tres estilos de animación para tres contextos distintos: celebratorio donde la energía es alta, moderado donde la comunidad observa, sutil donde el legado permanece. Porque los logros merecen ser vistos — pero la experiencia de comunidad siempre es lo primero.
          </p>
        </section>
      </main>
    </div>
  );
}

function AchievementDetailCard({
  achievement,
  isSelected,
  onClick,
}: {
  achievement: LiveAchievement;
  isSelected: boolean;
  onClick: () => void;
}) {
  const tc = achievementTypeConfig[achievement.achievementType];
  const lc = locationConfig[achievement.location];

  return (
    <div
      className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'bg-teal-50 border-teal-300/60 shadow-sm'
          : 'bg-background-50 border-background-200/70 hover:border-teal-300/60'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="relative shrink-0">
          <img src={achievement.userPhoto} alt={achievement.userName} className="w-10 h-10 rounded-full object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${tc.bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${tc.icon} text-[9px] ${tc.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <TypeBadge type={achievement.achievementType} />
            <LocationBadge location={achievement.location} />
            {achievement.isRecent && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-emerald-100 text-emerald-700">
                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                AHORA
              </span>
            )}
            <span className="text-[9px] text-foreground-400 ml-auto shrink-0">{achievement.achievedAtRelative}</span>
          </div>
          <p className="text-xs font-bold text-foreground-950">
            {achievement.userName} <span className="text-foreground-400 font-normal">{tc.verb}</span> {achievement.title}
          </p>
          <p className="text-[10px] text-foreground-500">{achievement.subtitle}</p>

          {isSelected && (
            <div className="mt-3 pt-3 border-t border-background-200/50 animate-fadeIn space-y-2">
              <p className="text-[11px] text-foreground-700">{achievement.description}</p>

              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className={`text-xs font-extrabold ${tc.color}`}>{achievement.value}</p>
                  <p className="text-[8px] text-foreground-400">Valor</p>
                </div>
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className="text-xs font-extrabold text-foreground-950">{achievement.reactions}</p>
                  <p className="text-[8px] text-foreground-400">Reacciones</p>
                </div>
                <div className="p-2 rounded-lg bg-background-100 text-center">
                  <p className="text-xs font-extrabold text-foreground-950">{achievement.comments}</p>
                  <p className="text-[8px] text-foreground-400">Comentarios</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-foreground-400 flex items-center gap-1">
                  <i className="ri-user-line text-[9px]"></i>
                  Nivel {achievement.userLevel}
                </span>
                <span className="text-foreground-400 flex items-center gap-1">
                  <i className="ri-map-pin-line text-[9px]"></i>
                  {lc.label}
                </span>
                <span className="text-foreground-400 flex items-center gap-1">
                  <i className="ri-timer-line text-[9px]"></i>
                  Decae en ~{achievement.decayMinutes >= 1440 ? `${Math.floor(achievement.decayMinutes / 1440)}d` : achievement.decayMinutes >= 60 ? `${Math.floor(achievement.decayMinutes / 60)}h` : `${achievement.decayMinutes}m`}
                </span>
              </div>

              {achievement.relatedEntityName && (
                <div className="flex items-center gap-1.5 text-[10px] text-foreground-500">
                  <i className="ri-link text-[9px]"></i>
                  Relacionado con: <span className="font-semibold text-foreground-700">{achievement.relatedEntityName}</span>
                </div>
              )}

              {achievement.previousValue && (
                <p className="text-[10px] text-foreground-400">
                  <span className="line-through mr-1">{achievement.previousValue}</span>
                  <i className="ri-arrow-right-line text-[8px] mx-1"></i>
                  <span className="font-semibold text-foreground-600">{achievement.value}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}